/**
 * Navigation Store
 *
 * Zustand-based store for navigation with command-driven architecture.
 *
 * Usage in components:
 * ```typescript
 * import { useNavigationDispatch, useNavigationSelector, createNavigationPolicies } from '@domains/navigation';
 * import { useNavigate, useLocation } from 'react-router';
 *
 * function Layout() {
 *   const navigate = useNavigate();
 *   const location = useLocation();
 *   const dispatch = useNavigationDispatch();
 *
 *   // Initialize policies with React Router's navigate
 *   useNavigationPolicies( createNavigationPolicies( navigate ));
 *
 *   // Sync location changes
 *   useEffect(() => {
 *     dispatch(
 *       {
 *         type: 'navigation/location/update',
 *         payload: { path: location.pathname, params: location.state || {} }
 *       },
 *       { source: 'url', timestamp: Date.now() }
 *     );
 *   }, [ location, dispatch ]);
 *
 *   // Navigate via command
 *   const handleNavigate = ( path: string ) => {
 *     dispatch(
 *       { type: 'navigation/request/navigate', payload: { path } },
 *       { source: 'user', timestamp: Date.now() }
 *     );
 *   };
 * }
 * ```
 */

import { createStore } from '@commandDrivenStateEngine/core/createStore';
import { NavigateFunction } from 'react-router';
import { initialState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { createNavigationPolicy } from './policies/navigationPolicy';

// Create the store
const {
  useSelector,
  useErrors,
  useDispatch,
  usePolicies,
  getState,
  dispatch
} = createStore({
  name: 'navigation',
  initialState,
  handlers,
  invariants
});

/**
 * Policy factory function.
 * Creates policies with React Router's navigate function.
 *
 * @param navigate - React Router's navigate function from useNavigate()
 */
export const createNavigationPolicies = ( navigate: NavigateFunction ) => [
  createNavigationPolicy( navigate )
];

// Export hooks with domain-specific names
export {
  useSelector as useNavigationSelector,
  useErrors as useNavigationErrors,
  useDispatch as useNavigationDispatch,
  usePolicies as useNavigationPolicies,
  getState as getNavigationState,
  dispatch as dispatchNavigationCommand
};

// Re-export selectors
export { selectors } from './state';
